import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Loader2, CloudOff, Cloud, Shield } from 'lucide-react';
import type { AutoSaveStatusInfo } from '../services/AutoSaveService';

interface AutoSaveStatusIndicatorProps {
  statusInfo: AutoSaveStatusInfo;
}

/**
 * 自动保存状态指示器（页面左下角固定位置）
 */
export default function AutoSaveStatusIndicator({ statusInfo }: AutoSaveStatusIndicatorProps) {
  const { status, lastSavedTime, errorMessage } = statusInfo;

  const formatTime = (date: Date | null): string => {
    if (!date) return '';
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'idle':
        return {
          icon: <Shield className="w-3.5 h-3.5" />,
          text: '自动保存已启用',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-500',
          iconColor: 'text-gray-400',
        };
      case 'saved':
        return {
          icon: <CheckCircle className="w-3.5 h-3.5" />,
          text: `已于 ${formatTime(lastSavedTime)} 保存`,
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-200',
          textColor: 'text-emerald-700',
          iconColor: 'text-emerald-500',
        };
      case 'saving':
        return {
          icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
          text: '保存中...',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-700',
          iconColor: 'text-blue-500',
        };
      case 'error':
        return {
          icon: <CloudOff className="w-3.5 h-3.5" />,
          text: errorMessage || '网络异常，已本地缓存',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-700',
          iconColor: 'text-amber-500',
        };
      case 'syncing':
        return {
          icon: <Cloud className="w-3.5 h-3.5 animate-pulse" />,
          text: '正在同步本地缓存...',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-700',
          iconColor: 'text-blue-500',
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={status}
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -5, scale: 0.95 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={`fixed bottom-4 left-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border shadow-sm backdrop-blur-sm ${config.bgColor} ${config.borderColor} ${config.textColor}`}
      >
        <span className={config.iconColor}>{config.icon}</span>
        <span className="text-xs font-medium whitespace-nowrap">{config.text}</span>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * 自动保存状态指示器（Header内联紧凑版）
 * 用于放置在倒计时左侧的紧凑布局
 */
export function AutoSaveStatusIndicatorInline({ statusInfo }: AutoSaveStatusIndicatorProps) {
  const { status, lastSavedTime, errorMessage } = statusInfo;

  const formatTime = (date: Date | null): string => {
    if (!date) return '';
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'idle':
        return {
          icon: <Shield className="w-3.5 h-3.5" />,
          text: '自动保存已启用',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-500',
          iconColor: 'text-gray-400',
        };
      case 'saved':
        return {
          icon: <CheckCircle className="w-3.5 h-3.5" />,
          text: `已于 ${formatTime(lastSavedTime)} 保存`,
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-200',
          textColor: 'text-emerald-700',
          iconColor: 'text-emerald-500',
        };
      case 'saving':
        return {
          icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
          text: '保存中...',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-700',
          iconColor: 'text-blue-500',
        };
      case 'error':
        return {
          icon: <CloudOff className="w-3.5 h-3.5" />,
          text: errorMessage || '网络异常，已本地缓存',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-700',
          iconColor: 'text-amber-500',
        };
      case 'syncing':
        return {
          icon: <Cloud className="w-3.5 h-3.5 animate-pulse" />,
          text: '正在同步本地缓存...',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-700',
          iconColor: 'text-blue-500',
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={status}
        initial={{ opacity: 0, x: -10, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 10, scale: 0.95 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={`flex items-center gap-1.5 px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg border text-xs lg:text-sm font-medium whitespace-nowrap ${config.bgColor} ${config.borderColor} ${config.textColor}`}
      >
        <span className={config.iconColor}>{config.icon}</span>
        <span className="hidden sm:inline">{config.text}</span>
        <span className="sm:hidden">{config.icon}</span>
      </motion.div>
    </AnimatePresence>
  );
}
